import { Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { ProctorService } from './proctor.service';
import { UtilService } from './util.service';
import { environment } from 'src/environments/environment';
import { HttpHeaders } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class AiProctorService {

  stopped: boolean;
  private video: HTMLVideoElement;
  private videoStream: MediaStream;
  private canvas: HTMLCanvasElement;
  private _aiImageAnalysisTimeout: any;
  private _aiNoiseAnalysisTimeout: any;
  private readonly _noiseLevelThreshold: number = 30;
  private audioVisualiserJavascriptNode: ScriptProcessorNode;
  private analyser: AnalyserNode;

  private canFlagForNoise: boolean;

  aiFlagConfig: AIFlagConfig[];

  onImageAnalysis: Subject<any> = new Subject();
  onNoiseAnalysis: Subject<any> = new Subject();

  req_headers = new HttpHeaders({ "content-type": "application/json", "accept": "application/json" });

  constructor(private utilService: UtilService) {
    this.video = document.createElement('video');
    this.video.autoplay = true;
    this.video.muted = true;
    this.video.style.display = 'none';
    this.video.width = 480;
    this.video.height = 360;
    this.canvas = document.createElement("canvas");
  }

  private async startVideo(videostream: MediaStream) {
    this.video.srcObject = videostream;
    await this.video.play();
  }

  /**
  * Initialize and start the AI proctor.
  * @param faceSnapCount number of candidate's facial data to keep in memory
  * @param faceSnapInterval interval between each snapshot in milliseconds (ms)
  * @param candidateFaceData candidate's image
  */
  async startAiProctor(videoStream: MediaStream, overrideai: boolean, attempt: number, examid: number, passportversion: number, passporttype: string, passporturl: string, username: string, scheduleid: number, company: string) {
    this.stopped = false;
    this.canFlagForNoise = true;

    await this.startAIProctorVideo(videoStream);

    //this.aiFlagConfig = await this.proctorService.getAIFlagConfig();

    const aiAnalysis = async () => {
      try {
        if (!this.stopped) {
          const data = this.utilService.takeVideoSnapshot(this.video, this.canvas);
          const analysis = await this.getAiImageAnalysis(data, overrideai, attempt, examid, passportversion, passporttype, passporturl, username, scheduleid, company) as any;
          this.onImageAnalysis.next(analysis);
        }
      }
      finally {
        if (!this.stopped) {
          this._aiImageAnalysisTimeout = setTimeout(aiAnalysis, 10 * 1000);
        }
      }
    };

    this.startNoiseAnalysis(this.videoStream);
    this._aiImageAnalysisTimeout = setTimeout(aiAnalysis, 1000);
    console.log("Ai-Proctor started")
  }

  public async startAIProctorVideo(videoStream: MediaStream) {
    this.videoStream = videoStream;
    await this.startVideo(this.videoStream);
    this.canvas.width = this.video.videoWidth;
    this.canvas.height = this.video.videoHeight;
  }

  stopAiProctor() {
    try {
      clearTimeout(this._aiImageAnalysisTimeout);
      clearTimeout(this._aiNoiseAnalysisTimeout);

      this.stopped = true;
      this.canFlagForNoise = false;
      this.onImageAnalysis = new Subject();
      this.onNoiseAnalysis = new Subject();
      this.audioVisualiserJavascriptNode?.removeEventListener("audioprocess", this.getNoiseLevel);
    }
    finally {
      this.video.pause();
      this.video.srcObject = null;
      this.videoStream = null;
      this.audioVisualiserJavascriptNode = null;
      console.log("Ai-Proctor stopped")
    }
  }

  private startNoiseAnalysis(stream: MediaStream) {
    const audioContext = new AudioContext();
    this.analyser = audioContext.createAnalyser();

    const microphone = audioContext.createMediaStreamSource(stream);
    this.audioVisualiserJavascriptNode = audioContext.createScriptProcessor(2048, 1, 1);

    this.analyser.smoothingTimeConstant = 0.8;
    this.analyser.fftSize = 1024;

    microphone.connect(this.analyser);
    this.analyser.connect(this.audioVisualiserJavascriptNode);
    this.audioVisualiserJavascriptNode.connect(audioContext.destination);

    this.audioVisualiserJavascriptNode.addEventListener("audioprocess", this.getNoiseLevel);
  }

  getNoiseLevel = () => {
    var array = new Uint8Array(this.analyser?.frequencyBinCount);
    this.analyser.getByteFrequencyData(array);
    let values = 0;

    let length = array.length;
    for (var i = 0; i < length; i++) {
      values += (array[i]);
    }

    let noiseLevel = values / length;
    if (this.canFlagForNoise && noiseLevel > this._noiseLevelThreshold) {
      this.canFlagForNoise = false;
      this.onNoiseAnalysis.next(noiseLevel);
      console.log(noiseLevel);
      if (!this.stopped) {
        this._aiNoiseAnalysisTimeout = setTimeout(this.resetCanFlagForNoise, 30 * 1000);
      }
    }
  }

  resetCanFlagForNoise = () => {
    if (!this.stopped) {
      this.canFlagForNoise = true;
      console.log('resCanFlag called');
    }
  };

  private async getImageAnalysis(donotsendflagstoserver = false, force: boolean = false, attempt: number, examid: number, passportversion: number, passporttype: string, passporturl: string, username: string, scheduleid: number, company: string) {
    if (this.stopped && !force) {
      throw new Error('AI proctor service is not running');
    }

    const data = this.utilService.takeVideoSnapshot(this.video, this.canvas);
    return await this.getAiImageAnalysis(data, donotsendflagstoserver, attempt, examid, passportversion, passporttype, passporturl, username, scheduleid, company) as any;
  }

  async aiPreventExamStart(force: boolean = false, attempt: number, examid: number, passportversion: number, passporttype: string, passporturl: string, username: string, scheduleid: number, company: string): Promise<AIPreventExamStartResponse> {
    const res: AIPreventExamStartResponse = {
      reasons: [],
      preventstart: false
    };
    // const preventStartFlags: Set<AIFlags> = new Set();

    // this.aiFlagConfig.forEach(flag => {
    //   if(flag.preventstart){
    //     preventStartFlags.add(flag.aiflag);
    //   }
    // });

    //all anomalous conditions should prevent starting
    let i = 0;
    do{
      try {
        i++;
        const { brightnessAnalysis, faceAnalysis } = await this.getImageAnalysis(true, force, attempt, examid, passportversion, passporttype, passporturl, username, scheduleid, company);

        // if(brightnessAnalysis == 'low'){// && preventStartFlags.has(AIFlags.LowLight)){      
        //   res.reasons.push("Low light detected");
        // }
        // else if(brightnessAnalysis == 'high'){// && preventStartFlags.has(AIFlags.HighIntensity)){
        //   res.reasons.push("High intensity light detected");
        // }

        if (faceAnalysis.noFace) {// && preventStartFlags.has(AIFlags.NoFace)){
          res.reasons.push("No face detected");
        }
        else {
          if (faceAnalysis.multipleFaces) {// && preventStartFlags.has(AIFlags.MultipleFaces)){
            res.reasons.push("Multiple faces detected");
          }
          if (faceAnalysis.candidateFaceIsNotAMatch) {// && preventStartFlags.has(AIFlags.FaceMismatch)){
            res.reasons.push("Candidate face mismatch detected");
          }
        }
        res.preventstart = res.reasons.length > 0;

        break;
      }
      catch (error) {
        console.error('Error while checking aiPreventExamStart');
        console.error(error);

        await this.utilService.sleep(10 * 1000);
      }
    }
    while(i <= 3);
    
    return res;
  }

  async confirmVideoHasFace(): Promise<boolean> {
    //we only ever need to do this when we have skipped AI passport check
    //this is because AI passport check requires a facial check of the video to succeed
    const imgDataURL = this.utilService.takeVideoSnapshot(this.video, this.canvas);
    const ret = await this.utilService.tryPost(`${environment.aiServerUrl}checkface`, { imgDataURL }, { headers: this.req_headers });
    return !!ret?.hasface;
  }

  async getAiImageAnalysis(imgDataURL, overrideai: boolean, attempt: number, examid: number, passportversion: number, passporttype: string, passporturl: string, username: string, scheduleid: number, company: string) {
    console.log("overrideai is " + overrideai);
    const candidate = { examid: examid, company: company, username: username, attempt: attempt, overrideai: overrideai, passportversion: passportversion, passporttype: passporttype, passporturl: passporturl, scheduleid: scheduleid };
    const ret = await this.utilService.tryPost(`${environment.aiServerUrl}`, { candidate, imgDataURL }, { headers: this.req_headers });
    return ret;
  }

  async sendpenaltynotification(penaltyresponse: any[], cbtWindow: any) {
    penaltyresponse.forEach(penalty => {
      if (penalty.penalized) {
        cbtWindow.postMessage({ event: 'aipenalty', message: this.getPenaltyMessage(penalty.penalty, penalty.reason) }, '*');
      }
    });
  }

  private getPenaltyMessage(penalty: string, reason: string) {
    let reasonMsg = '';
    let penaltyMsg = '';
    switch (reason) {
      case 'NoFace':
        reasonMsg = `'No face detected'`;
        break;
      case 'MultipleFaces':
        reasonMsg = `'Multiple faces detected'`;
        break;
      case 'LowLight':
        reasonMsg = `'Low light detected'`;
        break;
      case 'HighIntensity':
        reasonMsg = `'High intensity light detected'`;
        break;
      case 'FaceMismatch':
        reasonMsg = `'Face mis-match detected'`;
        break;
    }

    switch (penalty) {
      case 'Restart':
        penaltyMsg = `Your exam has been restarted because ${reasonMsg}`;
        break;
      case 'ReduceTime':
        penaltyMsg = `Your time has been reduced because ${reasonMsg}`;
        break;
      case 'EndExam':
        penaltyMsg = `Your exam has been stopped because ${reasonMsg}`;
        break;
    }

    return penaltyMsg;
  }
}

export interface AIFlagConfig {
  examid: string,
  company: string,
  threshold: number,
  preventstart: boolean,
  minutestoreduce: number,
  maxnoofrestarts: number,
  aiflag: AIFlags,
  penalty: string
}

export interface AIPreventExamStartResponse {
  reasons: string[];
  preventstart: boolean;
}

export enum AIFlags {
  LowLight = 'Lowlight',
  MultipleFaces = 'MultipleFaces',
  NoFace = 'NoFace',
  HighIntensity = 'HighIntensity',
  FaceMismatch = 'FaceMismatch',
  Noise = 'Noise'
}